// src/components/admin/RichTextEditor.jsx
import React, { useMemo, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useToast } from './ToastMessage';
import { uploadImage } from '../../apis/uploadImage';
import './RichTextEditor.scss';

// Đăng ký ClassAttributor để Quill KHÔNG BAO GIỜ tự động xóa thuộc tính class="..."
try {
    const Parchment = Quill.import('parchment');
    if (Parchment) {
        const ClassAttribute = new Parchment.Attributor.Class('class', 'class', {
            scope: Parchment.Scope.INLINE
        });
        const BlockClassAttribute = new Parchment.Attributor.Class('class', 'class', {
            scope: Parchment.Scope.BLOCK
        });
        Quill.register(ClassAttribute, true);
        Quill.register(BlockClassAttribute, true);
    }
} catch (err) {
    console.warn('[RichTextEditor] Parchment ClassAttributor warning:', err);
}

/**
 * RichTextEditor — Trình soạn thảo văn bản phong phú dựa trên Quill.
 *
 * Props:
 *   @param {string}   value       - Nội dung HTML hiện tại của editor.
 *   @param {Function} onChange    - Callback khi nội dung thay đổi (nhận HTML string).
 *   @param {string}   [placeholder] - Placeholder text hiển thị khi editor trống.
 */
const RichTextEditor = ({ value, onChange, placeholder = 'Nhập nội dung...' }) => {
    const toast = useToast();
    const quillRef = useRef(null);
    // [Fix 1] imageHandlerRef giữ tham chiếu đến handler mới nhất.
    // useMemo dưới sẽ gọi qua ref này thay vì cầm trực tiếp hàm,
    // tránh "stale closure" khi deps=[].
    const imageHandlerRef = useRef(null);
    // Ref cho paste handler — cùng pattern với imageHandlerRef
    const pasteHandlerRef = useRef(null);

    /**
     * Custom Image Handler:
     * Chặn hành vi mặc định của Quill (lưu ảnh dạng Base64).
     * Mở file dialog, upload file lên API Backend, sau đó chèn URL trả về vào editor.
     */
    // [Fix 1] Gán vào .current mỗi render → ref luôn chứa phiên bản mới nhất
    imageHandlerRef.current = () => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        // [Fix 2] Lưu range TRƯỜC khi click() làm editor mất focus.
        // Nếu gọi getSelection() bên trong onchange (sau khi user chọn file),
        // editor đã mất focus → trả về null → range.index crash.
        const savedRange = editor.getSelection() || { index: editor.getLength() - 1, length: 0 };

        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        // Sử dụng style ẩn an toàn thay vì display: none để tránh browser block sự kiện click
        input.style.position = 'absolute';
        input.style.visibility = 'hidden';
        input.style.width = '0';
        input.style.height = '0';
        document.body.appendChild(input);

        input.onchange = async () => {
            document.body.removeChild(input);

            const file = input.files?.[0];
            if (!file) return;

            // [Fix 3] Dùng hằng số để insertText & deleteText luôn khớp nhau.
            // Bản cũ hardcode số 18 nhưng chuỗi có 20 ký tự → xóa thiếu, ảnh bị lệch vị trí.
            const PLACEHOLDER = '⏳ Đang tải ảnh...';

            editor.focus();
            editor.setSelection(savedRange.index, 0);
            editor.insertText(savedRange.index, PLACEHOLDER);

            try {
                const imageUrl = await uploadImage(file);

                editor.deleteText(savedRange.index, PLACEHOLDER.length);
                editor.insertEmbed(savedRange.index, 'image', imageUrl);
                editor.setSelection(savedRange.index + 1);

                toast.success('Upload ảnh thành công!');
            } catch (error) {
                editor.deleteText(savedRange.index, PLACEHOLDER.length);
                editor.setSelection(savedRange.index);

                const errorMessage = error?.message || 'Upload ảnh thất bại. Vui lòng thử lại.';
                toast.error(errorMessage);
                console.error('[RichTextEditor] Upload image error:', error);
            }
        };

        // Fallback: Xóa input nếu user hủy chọn file (cửa sổ dialog đóng lại mà không chọn)
        window.addEventListener('focus', () => {
            setTimeout(() => {
                if (document.body.contains(input)) {
                    document.body.removeChild(input);
                }
            }, 1000);
        }, { once: true });

        input.click();
    };

    /**
     * Paste Image Handler:
     * Khi user Ctrl+V / Cmd+V một ảnh (từ clipboard hoặc screenshot),
     * ngăn Quill chèn Base64, thay vào đó upload file lên Cloudinary.
     */
    pasteHandlerRef.current = async (e) => {
        const clipboard = e.clipboardData;
        if (!clipboard) return;

        // Tìm item ảnh trong clipboard (bao gồm cả screenshot và ảnh copy từ web)
        const items = Array.from(clipboard.items || []);
        const imageItem = items.find(item => item.type.startsWith('image/'));
        if (!imageItem) return; // Không có ảnh → để Quill xử lý bình thường (paste text, v.v.)

        // Chặn Quill tự chèn Base64
        e.preventDefault();
        e.stopPropagation();

        const file = imageItem.getAsFile();
        if (!file) return;

        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        // Lấy vị trí con trỏ hiện tại (editor vẫn còn focus khi paste)
        const savedRange = editor.getSelection() || { index: editor.getLength() - 1, length: 0 };
        const PLACEHOLDER = 'Đang tải ảnh...';

        editor.focus();
        editor.setSelection(savedRange.index, 0);
        editor.insertText(savedRange.index, PLACEHOLDER);

        try {
            const imageUrl = await uploadImage(file);

            editor.deleteText(savedRange.index, PLACEHOLDER.length);
            editor.insertEmbed(savedRange.index, 'image', imageUrl);
            editor.setSelection(savedRange.index + 1);

            toast.success('Dán ảnh thành công!');
        } catch (error) {
            editor.deleteText(savedRange.index, PLACEHOLDER.length);
            editor.setSelection(savedRange.index);

            toast.error(error?.message || 'Upload ảnh thất bại. Vui lòng thử lại.');
            console.error('[RichTextEditor] Paste image error:', error);
        }
    };

    /**
     * Dùng useMemo để tránh re-create object modules mỗi lần render.
     * Đây là bắt buộc để ngăn editor bị re-render & mất focus khi user gõ phím.
     */
    const modules = useMemo(() => ({
        toolbar: {
            container: [
                // Tiêu đề
                [{ header: [1, 2, 3, false] }],
                // Định dạng văn bản
                ['bold', 'italic', 'underline', 'strike'],
                // Màu chữ & màu nền
                [{ color: [] }, { background: [] }],
                // Căn lề
                [{ align: [] }],
                // Danh sách
                [{ list: 'ordered' }, { list: 'bullet' }],
                // Thụt lề
                [{ indent: '-1' }, { indent: '+1' }],
                // Media & Links
                ['link', 'image', 'video'],
                // Xoá định dạng
                ['clean'],
            ],
            handlers: {
                // [Fix 1] Gọi qua ref thay vì trực tiếp → luôn dùng phiên bản mới nhất
                image: () => imageHandlerRef.current?.(),
            },
        },
        clipboard: {
            // Giữ lại các tag HTML khi paste
            matchVisual: false,
        },
    }), []); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Định nghĩa các format được phép sử dụng trong editor.
     */
    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'align',
        'list', 'bullet', 'indent',
        'link', 'image', 'video',
    ];

    return (
        <div className="quill-custom-container" onPasteCapture={(e) => pasteHandlerRef.current?.(e)}>
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value || ''}
                onChange={onChange}
                modules={modules}
                formats={formats}
                placeholder={placeholder}
            />
        </div>
    );
};

export default RichTextEditor;
